import React from 'react';
import ENVIRONMENT_VARIABLES from 'shared/utils/environmentVariables';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import Logger from 'shared/lib/analytics/logger';
import ERROR_CODE from 'shared/utils/errorCodes';
import { EVENT_TYPES } from '../../lib/analytics/constants';

const MAX_REPORTS_PER_SESSION = 10;
// Cross-origin scripts (GTM, game providers) report errors with no detail at all.
const CROSS_ORIGIN_SCRIPT_ERROR = 'Script error.';

const serializeError = error => {
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error, Object.getOwnPropertyNames(error || {}));
  } catch (e) {
    return String(error);
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: {},
    };
  }

  reportsSent = 0;
  reportedErrors = new Set();

  componentDidMount() {
    if (ENVIRONMENT_VARIABLES.DIVE_ERROR_HANDLING === 'yes') {
      window.addEventListener('error', this.handleWindowError);
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleWindowError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentDidCatch(error, info) {
    this.reportError(ERROR_CODE.GLOBAL_REACT_RENDER_ERROR, error, { body: info?.componentStack });
  }

  handleWindowError = event => {
    // Resource load failures (img, script) also land here without an error object.
    if (!event.error && (!event.message || event.message === CROSS_ORIGIN_SCRIPT_ERROR)) return;
    this.reportError(ERROR_CODE.GLOBAL_UNCAUGHT_JS_ERROR, event.error || event.message, {
      url: event.filename,
    });
  };

  handleUnhandledRejection = event => {
    this.reportError(ERROR_CODE.GLOBAL_UNHANDLED_PROMISE_REJECTION, event.reason);
  };

  reportError = (code, error, extra = {}) => {
    // Reporting must never throw from inside the global handlers.
    try {
      const dedupeKey = `${code}:${error?.message || error}`;
      if (this.reportsSent >= MAX_REPORTS_PER_SESSION || this.reportedErrors.has(dedupeKey)) return;
      this.reportedErrors.add(dedupeKey);
      this.reportsSent += 1;

      Logger.sendEvent(EVENT_TYPES.FRONTEND_ERROR, {
        error: serializeError(error),
        currentScreen: window.location.pathname,
        ...extra,
        code,
      });
    } catch (e) {
      console.error(e);
    }
  };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    const {
      hasError,
      error: { message },
    } = this.state;

    const { children } = this.props;

    if (!hasError) return children;

    const isDevelopmentMode = false;
    return isDevelopmentMode ? (
      <div>{message}</div>
    ) : (
      <div
        style={{
          background: `url(${assetSource(ASSET_KEY.ERROR_BACKGROUND)})`,
          width: '100%',
          height: '100%',
          position: 'fixed',
          top: 0,
          right: 0,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          alt=""
          src={assetSource(ASSET_KEY.MESSAGE)}
          style={{
            width: '80%',
            height: '80%',
            objectFit: 'contain',
          }}
        />
      </div>
    );
  }
}

export { ErrorBoundary };

